import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Stack, TextField, Button, IconButton, Tooltip, InputAdornment, Alert,
    TablePagination, Snackbar,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import { useCrumbActionsSetter } from '../../utils/crumbActions';
import { card, field, th, td, EmptyState, SkeletonRows, LoadAlert } from '../uiKit';
import http, { apiErrorMessage } from '../../Api/http';
import { GetBiometricAttendanceLogs, GetSyncSummary } from '../../Api/Api';

const pad = (n) => String(n).padStart(2, '0');
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const isoMonthStart = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; };
// <input type="date"> gives yyyy-MM-dd; the API wants dd-MM-yyyy.
const toDMY = (iso) => { const [y, m, d] = String(iso).split('-'); return `${d}-${m}-${y}`; };

// Columns we know how to label and want first; anything else the server sends is appended.
const PREFERRED = [
    ['employeeName', 'Employee'], ['employeeCode', 'Employee Code'], ['biometricId', 'Biometric ID'],
    ['deviceUserId', 'Device User ID'], ['userId', 'User ID'], ['enrollNo', 'Enroll No'],
    ['punchTime', 'Time'], ['eventTime', 'Time'], ['logTime', 'Time'], ['timestamp', 'Time'], ['dateTime', 'Time'],
    ['status', 'Status'], ['direction', 'Direction'], ['punchType', 'Punch Type'], ['verifyMode', 'Verify Mode'],
    ['deviceName', 'Device'], ['deviceId', 'Device ID'], ['deviceSerial', 'Device Serial'], ['deviceIp', 'Device IP'],
];
const SKIP = new Set(['id', 'entityId', 'createdOn', 'createdBy']);
const humanize = (k) => k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
const cell = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
};

const pickList = (body) => {
    const d = body?.data;
    if (Array.isArray(d)) return d;
    if (d && typeof d === 'object') {
        const arr = d.logs ?? d.items ?? d.records ?? d.rows;
        if (Array.isArray(arr)) return arr;
    }
    return [];
};

export default function BiometricLogsTab() {
    const [from, setFrom] = useState(isoMonthStart());
    const [to, setTo] = useState(isoToday());
    const [logs, setLogs] = useState([]);
    const [, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [snack, setSnack] = useState(null);

    const rangeOk = Boolean(from && to && from <= to);

    const load = useCallback(async () => {
        if (!rangeOk) return;
        setLoading(true);
        setError('');
        const params = { fromDate: toDMY(from), toDate: toDMY(to) };
        const [logsRes, sumRes] = await Promise.allSettled([
            http.get(GetBiometricAttendanceLogs, { params }),
            http.get(GetSyncSummary, { params }),
        ]);
        if (logsRes.status === 'fulfilled') {
            const body = logsRes.value.data;
            if (body?.error) setError(body.message || 'Could not load biometric logs.');
            else setLogs(pickList(body));
        } else {
            setLogs([]);
            if (logsRes.reason?.response?.status !== 404) setError(apiErrorMessage(logsRes.reason, 'Could not load biometric logs.'));
        }
        if (sumRes.status === 'fulfilled' && !sumRes.value.data?.error) setSummary(sumRes.value.data?.data ?? null);
        else {
            setSummary(null);
            if (sumRes.status === 'rejected' && sumRes.reason?.response?.status !== 404) {
                setSnack(apiErrorMessage(sumRes.reason, 'Could not load the sync summary.'));
            }
        }
        setPage(0);
        setLoading(false);
    }, [from, to, rangeOk]);

    useEffect(() => { load(); }, [load]);

    const columns = useMemo(() => {
        const present = new Set();
        logs.slice(0, 200).forEach((r) => Object.keys(r || {}).forEach((k) => present.add(k)));
        const cols = [];
        const labelsUsed = new Set();
        PREFERRED.forEach(([k, label]) => {
            if (present.has(k) && !labelsUsed.has(label)) { cols.push({ key: k, label }); labelsUsed.add(label); present.delete(k); }
        });
        [...present].filter((k) => !SKIP.has(k)).forEach((k) => cols.push({ key: k, label: humanize(k) }));
        return cols;
    }, [logs]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return logs;
        return logs.filter((r) => Object.values(r || {}).some((v) => cell(v).toLowerCase().includes(q)));
    }, [logs, search]);

    const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // The date range and reload live at the right end of the breadcrumb row.
    const setCrumbActions = useCrumbActionsSetter();
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField type="date" label="From" size="small" value={from} onChange={(e) => setFrom(e.target.value)}
                    error={!rangeOk} sx={{ ...field, width: 160, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], bgcolor: '#fff' } }} slotProps={{ inputLabel: { shrink: true } }} />
                <TextField type="date" label="To" size="small" value={to} onChange={(e) => setTo(e.target.value)}
                    error={!rangeOk} sx={{ ...field, width: 160, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], bgcolor: '#fff' } }} slotProps={{ inputLabel: { shrink: true } }} />
                <Tooltip arrow title="Reload">
                    <span><IconButton onClick={load} disabled={loading || !rangeOk} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', bgcolor: '#fff', height: 40, width: 40, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                        <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                    </IconButton></span>
                </Tooltip>
            </Box>,
        );
        return () => setCrumbActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, from, to, rangeOk, loading, load]);

    return (
        <Box sx={{ p: 2 }}>
            {!rangeOk && <Alert severity="warning" sx={{ mb: 2, borderRadius: '9px' }}>The "to" date must be on or after the "from" date.</Alert>}

            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ p: 1.5, gap: 1, alignItems: 'center', borderBottom: '1px solid #EEF0F6' }}>
                    <TextField size="small" placeholder="Search employee, device, status…" value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ ...field, width: { xs: '100%', sm: 320 } }}
                        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /></InputAdornment> } }} />
                    <Box sx={{ ml: 'auto', fontSize: 12, color: '#98A0AE' }}>{filtered.length} event{filtered.length === 1 ? '' : 's'}</Box>
                </Stack>

                {loading ? (
                    <SkeletonRows count={6} />
                ) : error ? (
                    <Box sx={{ p: 2 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{error}</LoadAlert>
                    </Box>
                ) : filtered.length === 0 ? (
                    <EmptyState icon={FingerprintRoundedIcon} title="No biometric events" hint="Nothing was reported by the devices in this date range." />
                ) : (
                    <>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr>{columns.map((c) => <Box component="th" key={c.key} sx={th}>{c.label}</Box>)}</tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((r, i) => (
                                        <Box component="tr" key={r.id ?? `${page}-${i}`} sx={{ '&:hover': { bgcolor: '#FAFAFE' } }}>
                                            {columns.map((c) => <Box component="td" key={c.key} sx={{ ...td, color: '#334155', whiteSpace: 'nowrap' }}>{cell(r[c.key])}</Box>)}
                                        </Box>
                                    ))}
                                </tbody>
                            </Box>
                        </Box>
                        <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                            rowsPerPageOptions={[25, 50, 100]} />
                    </>
                )}
            </Box>

            <Snackbar open={Boolean(snack)} autoHideDuration={3800} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity="warning" variant="filled" sx={{ borderRadius: '7px' }}>{snack}</Alert>
            </Snackbar>
        </Box>
    );
}
